import { resolveOpenerLocale } from "@loot-os/api-contract";
import { LootProvider } from "@loot-os/react";
import { Toaster } from "@loot-os/ui/components/sonner";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { BottomNav } from "./components/BottomNav.js";
import { CookieConsent } from "./components/CookieConsent.js";
import { ChatLauncher } from "./components/chat/ChatLauncher.js";
import { ChatPanel } from "./components/chat/ChatPanel.js";
import { IntercomLauncher } from "./components/IntercomLauncher.js";
import { ProfileProvider } from "./components/ProfileProvider.js";
import { SiteHeader } from "./components/SiteHeader.js";
import { SiteMenu } from "./components/SiteMenu.js";
import { SocialLinks } from "./components/SocialLinks.js";
import { requestBoxSearchFocus } from "./lib/boxSearchFocus.js";
import { setChatDrawerOpen, useChatAvailable } from "./lib/chat.js";
import { LOOT_PROXY_BASE, PROXIED_PUBLISHABLE_KEY } from "./lib/config.js";
import { legalParties } from "./lib/legal.js";
import { visibleNavItems } from "./lib/nav.js";
import { mintSessionToken } from "./lib/queries.js";
import { useShopper, useSignOut } from "./lib/shopper.js";
import { useTenant } from "./lib/tenant.js";
import { useTerminology } from "./lib/terminology.js";
import { toLootTheme } from "./lib/theme.js";

export function App() {
  const { sessionToken, client, authPending, sessionPending } = useShopper();
  const { config } = useTenant();
  const terms = useTerminology();
  // The opener's own labels ("Claim items") in the store's words; memoized so the provider's
  // value is stable across renders.
  const locale = useMemo(() => resolveOpenerLocale(terms), [terms]);
  // A store with chat off shows neither the tab nor the panel, not even briefly: see
  // `useChatAvailable` for why the config alone is not enough.
  const chat = useChatAvailable();

  return (
    <LootProvider
      publishableKey={PROXIED_PUBLISHABLE_KEY}
      baseUrl={LOOT_PROXY_BASE}
      // Signed in, share the app's client (one instance, one token). Signed
      // out it must stay unset — the opener's demo mode requires no client.
      client={sessionToken ? client : undefined}
      sessionToken={sessionToken}
      // Until the auth check and the token mint have both answered, the token's absence means
      // "unknown", not "signed out": the opener waits instead of offering a demo open.
      sessionPending={authPending || sessionPending}
      onSessionExpired={mintSessionToken}
      theme={toLootTheme(config.theme)}
      currency={config.currency}
      locale={locale}
    >
      {/* Inside the root route: the profile dialog links to the Account page, which needs the
          router; a provider above <RouterProvider> would have no router to link with. */}
      <ProfileProvider>
        {/* `overflow-x-clip` catches a row that runs to the screen's edge (Top hits) without
            making a scroll container, so the sticky header still sticks. Below `md` the page is
            padded by the tab bar's height. */}
        <div className="flex min-h-svh flex-col overflow-x-clip bg-background pb-[4.125rem] text-foreground md:pb-0">
          <Navigation chat={chat} />
          {/* Chat is fixed to the right edge of a wide screen, under the header, so it stays in view
              while the page scrolls (a drawer on a phone). It overlays the page rather than
              taking width from it. */}
          <div className="flex flex-1 flex-col">
            <main className="mx-auto w-full max-w-7xl min-w-0 flex-1 px-4 py-8">
              <Outlet />
            </main>
            <Footer />
          </div>
          {chat && <ChatPanel />}
          {chat && <ChatLauncher />}
          {/* Fixed-position, so where it sits in the tree is cosmetic; it needs both providers. */}
          <IntercomLauncher />
          {/* The one toast outlet: the forge's saves and refusals. Above the tab bar on a phone. */}
          <Toaster position="bottom-center" mobileOffset={{ bottom: "5.125rem" }} />
        </div>
      </ProfileProvider>
    </LootProvider>
  );
}

/**
 * The header, the menu flyout and the phone's tab bar, which share one menu: the header's menu
 * button and the tab bar's both open it.
 */
function Navigation({ chat }: { chat: boolean }) {
  const { user } = useShopper();
  const signOut = useSignOut();
  const { hasFeature } = useTenant();
  const terms = useTerminology();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const links = visibleNavItems(hasFeature, terms);
  const handleSignOut = () => signOut.mutate(undefined, { onSuccess: () => navigate({ to: "/" }) });
  // Search is the boxes page's: focus its field there, or go there and focus it.
  const handleSearch = () => {
    requestBoxSearchFocus();
    if (pathname !== "/boxes") void navigate({ to: "/boxes" });
  };

  return (
    <>
      <SiteHeader links={links} onOpenMenu={() => setMenuOpen(true)} onSearch={handleSearch} />
      <SiteMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        links={links}
        signedIn={Boolean(user)}
        onSignOut={handleSignOut}
        onSearch={handleSearch}
      />
      <BottomNav
        games={links.filter((link) => link.group === "games")}
        onOpenMenu={() => setMenuOpen(true)}
        // The tab bar shows below `md`, where chat is always the drawer.
        onOpenChat={chat ? () => setChatDrawerOpen(true) : undefined}
      />
    </>
  );
}

/**
 * The brand, socials and copyright beside one column per kind of link, then the small print
 * under a second rule. Stacked on a phone: the brand block first, the link columns side by side.
 */
function Footer() {
  const { config, hasFeature } = useTenant();
  const terms = useTerminology();
  const { legalName, year } = legalParties(config);

  return (
    <footer className="border-t border-border">
      <div className="mx-auto w-full max-w-7xl px-4">
        <div className="grid gap-8 py-9 md:grid-cols-4 md:gap-9">
          <div className="flex flex-col items-start gap-9 md:col-span-2">
            <Link to="/" className="flex items-center gap-2 text-base font-semibold tracking-tight">
              {config.branding.logoUrl ? (
                <img src={config.branding.logoUrl} alt="" className="h-6 w-auto" />
              ) : null}
              <span>{config.name}</span>
            </Link>
            <div className="flex flex-col items-start gap-4">
              <SocialLinks />
              <p className="text-sm text-muted-foreground">
                © {year} {legalName}. All rights reserved. Powered by Loot Labs.
              </p>
            </div>
          </div>
          <FooterColumn title="Store">
            {visibleNavItems(hasFeature, terms).map((item) => (
              <Link key={item.to} to={item.to} className="hover:underline">
                {item.label}
              </Link>
            ))}
          </FooterColumn>
          <FooterColumn title="Legal">
            <Link to="/terms" className="hover:underline">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:underline">
              Privacy Policy
            </Link>
            <CookieConsent />
          </FooterColumn>
        </div>
        <div className="flex flex-col gap-2 border-t border-border py-9 text-xs leading-relaxed text-muted-foreground">
          <p>
            {config.name} — every open is provably fair and verifiable on the{" "}
            <Link to="/fairness" className="underline">
              fairness page
            </Link>
            .
          </p>
          <p>
            All product and company names are trademarks or registered trademarks of their
            respective holders. Use of them does not imply any affiliation with or endorsement by
            them.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <nav aria-label={title} className="flex flex-col items-start gap-2.5">
      <h2 className="text-sm font-medium text-muted-foreground">{title}</h2>
      <div className="flex flex-col items-start gap-3 text-base text-foreground">{children}</div>
    </nav>
  );
}
